import React, { useContext, useEffect, useState } from "react";
import { Modal, Box, Typography, CircularProgress } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import NeoButton from "./NeoButton";
import PackOpeningModal from "./PackOpeningModal";
import TokenRewardModal from "./TokenRewardModal";
import { doc, updateDoc, getDoc, setDoc, increment } from "firebase/firestore";
import { db } from "../@shared/firebase/config";
import gameMenuBg from "../@shared/assets/game_menu.png";
import rewardIcon from "../@shared/assets/icons/reward_icon.png";
import tasksIcon from "../@shared/assets/icons/tasks_icon.png";
import { useAuthStore } from "../@shared/store/useAuthStore";
import { Close } from "@mui/icons-material";
import { MainContext } from "../App";
import CardRewardModal from "./CardRewardModal";

const dummyPoints = {
  damageDealtPoints: 100,
  eliminationPoints: 50,
  placementPoints: 300,
  playedCardPoints: 100,
  discardCardPoints: 50,
  tradeCardPoints: 75,
};

// Styles
const backdropStyle = {
  position: "fixed",
  top: 0,
  left: 0,
  width: "100vw",
  height: "100vh",
  backgroundColor: "rgba(0,0,0,0.6)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 1400,
};

const modalWrapperStyle = {
  width: "90vw",
  maxWidth: "500px",
  aspectRatio: 4 / 5,
  borderRadius: "16px",
  position: "relative",
};

const modalContentStyle = {
  width: "85%",
  height: "85%",
  display: "flex",
  flexDirection: "column",
  padding: "5% 8% 12%",
  backgroundImage: `url(${gameMenuBg})`,
  backgroundSize: "contain",
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",
  position: "relative",
};

const codeRowStyle = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  padding: "0 2%",
};

const scrollAreaStyle = {
  marginTop: "10px",
  overflowY: "auto",
  maxHeight: "80%",
};

const rewardRowStyle = {
  marginBottom: "12px",
  backgroundColor: "#452098",
  padding: "12px",
  borderRadius: "10px",
  display: "flex",
  alignItems: "center",
};

export default function QuestsAndRewardsModal({ visible, onClose }) {
  const { userData } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [rewards, setRewards] = useState([]);
  //   const [claiming, setClaiming] = useState(null);

  const [claimingReward, setClaimingReward] = useState(null); // holds reward being claimed
  const [packModalVisible, setPackModalVisible] = useState(false);
  const [tokenModalVisible, setTokenModalVisible] = useState(false);
  const [cardReward, setCardReward] = useState(null); // for card modals
  const [cardModalVisible, setCardModalVisible] = useState(false);
  const { Theme } = useContext(MainContext);

  useEffect(() => {
    if (visible) {
      // loadBackground();
      setLoading(true);

      const resolvedRewards = (userData?.rewards || []).map((reward) => {
        let name = "Mystery Reward";
        switch (reward.type) {
          case "card pack":
            name = "Infinity Card Pack";
            break;
          case "character card":
            name = `Character Card x${reward.amount}`;
            break;
          case "infinity card":
            name = `Infinity Card x${reward.amount}`;
            break;
          case "finite card":
            name = `Finite Card x${reward.amount}`;
            break;
          case "infinity tokens":
            name = `Infinity Tokens +${reward.amount}`;
            break;
        }

        return {
          ...reward,
          name,
        };
      });

      let sortedRewards = [...resolvedRewards].sort((x) =>
        x.claimed ? 1 : -1
      );

      setRewards(sortedRewards);

      setTimeout(() => setLoading(false), 600);
    }
  }, [visible, userData?.rewards]);

  const handleClaimReward = (reward) => {
    setClaimingReward(reward);

    switch (reward.type) {
      case "card pack":
        setPackModalVisible(true);
        break;
      case "infinity tokens":
        setTokenModalVisible(true);
        break;
      case "character card":
      case "finite card":
      case "infinity card":
        setCardReward(reward);
        setCardModalVisible(true);
        break;
      case "quest reward":
        if (reward.reward.currency === "Infinity Tokens") {
          setTokenModalVisible(true);
        } else if (reward.reward.currency === "XP") {
          finalizeClaim(reward.id);
        } else {
          console.warn("Unknown reward type");
        }
      default:
        console.warn("Unknown reward type");
    }
  };

  const finalizeClaim = async (rewardId) => {
    try {
      const reward = rewards.find((r) => r.id === rewardId); // must be in state
      if (!reward) {
        console.warn("Reward not found.");
        return;
      }

      const userId = userData.id;
      const rewardRef = doc(db, "users", userId, "rewards", rewardId);

      // 1. Always mark the reward as claimed
      await updateDoc(rewardRef, {
        claimed: true,
        claimedAt: Date.now(),
      });

      const amount = reward.amount || 1;
      const cardId = reward.reward?.cardId || reward.reward?.id;

      // 2. Conditional updates
      switch (reward.type) {
        case "infinity tokens": {
          const userDocRef = doc(db, "users", userId);
          await updateDoc(userDocRef, {
            infinityTokens: increment(amount),
          });
          break;
        }
        case "quest reward": {
          if (reward.reward.currency === "XP") {
            const userDocRef = doc(db, "users", userId);
            await updateDoc(userDocRef, {
              xp: increment(amount),
            });
            break;
          } else if (reward.reward.currency === "Infinity Tokens") {
            const userDocRef = doc(db, "users", userId);
            await updateDoc(userDocRef, {
              infinityTokens: increment(amount),
            });
            break;
          } else {
            console.warn("Unknown reward type");
          }
          break;
        }

        case "finite card": {
          if (!cardId)
            return console.warn("No card ID for finite card reward.");
          const cardRef = doc(db, "users", userId, "Infinity cards", cardId);
          const snap = await getDoc(cardRef);

          if (snap.exists()) {
            await updateDoc(cardRef, {
              amountOwned: increment(amount),
              new: true,
            });
          } else {
            await setDoc(cardRef, {
              ...reward.reward,
              amountOwned: amount,
              createdAt: Date.now(),
              lastUpdated: Date.now(),
              new: true,
            });
          }
          break;
        }

        case "infinity card": {
          if (!cardId)
            return console.warn("No card ID for infinity card reward.");
          const cardRef = doc(db, "users", userId, "Infinity cards", cardId);
          const snap = await getDoc(cardRef);

          if (snap.exists()) {
            await updateDoc(cardRef, {
              amountOwned: increment(amount),
              new: true,
            });
          } else {
            await setDoc(cardRef, {
              ...reward.reward,
              amountOwned: amount,
              createdAt: Date.now(),
              lastUpdated: Date.now(),
              new: true,
            });
          }
          break;
        }

        case "character card": {
          if (!cardId)
            return console.warn("No card ID for character card reward.");
          const cardRef = doc(db, "users", userId, "Character cards", cardId);
          const snap = await getDoc(cardRef);

          if (snap.exists()) {
            await updateDoc(cardRef, {
              amountOwned: increment(amount),
              new: true,
            });
          } else {
            await setDoc(cardRef, {
              ...reward.reward,
              amountOwned: amount,
              createdAt: Date.now(),
              lastUpdated: Date.now(),
              new: true,
            });
          }
          break;
        }

        case "card pack":
          // Already handled by PackOpeningModal
          break;

        default:
          console.warn("Unknown reward type:", reward.type);
      }

      setClaimingReward(null);
    } catch (err) {
      console.error("Error finalizing claim:", err);
    }
  };

  const CustomProgressBar = ({ progress, total }) => {
    const pct = Math.floor((progress / total) * 100);
    return (
      <Box
        sx={{
          width: "100%",
          bgcolor: "#553097",
          borderRadius: 1,
          overflow: "hidden",
          height: 8,
        }}
      >
        <Box sx={{ width: `${pct}%`, bgcolor: "#13ffaf", height: "100%" }} />
      </Box>
    );
  };

  const renderReward = (r) => (
    <Box key={r.id} sx={rewardRowStyle}>
      <Typography color="#fff" fontWeight={600} sx={{ flex: 1 }}>
        {r.name}
        <br />
        <span style={{ fontSize: 12 }}>{r?.description}</span>
      </Typography>
      {r.claimed ? (
        <Typography color="#0f0">Claimed</Typography>
      ) : (
        <NeoButton
          text="Claim"
          width={30}
          color="$primary"
          onClick={() => handleClaimReward(r)}
        />
      )}
    </Box>
  );

  return (
    <>
      <Modal open={visible} onClose={onClose} closeAfterTransition>
        <AnimatePresence>
          {visible && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              style={backdropStyle}
            >
              <motion.div
                initial={{ y: -50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: -50, opacity: 0 }}
                transition={{ duration: 0.3 }}
                style={modalWrapperStyle}
              >
                <Box sx={modalContentStyle}>
                  {/* Title */}
                  <Typography
                    color="#fff"
                    fontWeight="bold"
                    variant="h5"
                    align="center"
                    sx={{ mt: 1, mb: 1 }}
                  >
                    My Rewards
                  </Typography>
                  {/* Content */}
                  {loading ? (
                    <Box
                      sx={{
                        py: 2,
                        display: "flex",
                        alignItems: "center",
                        flexDirection: "column",
                      }}
                    >
                      <CircularProgress color="inherit" />
                      <Typography color="#fff" sx={{ mt: 1 }}>
                        Loading...
                      </Typography>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        ...scrollAreaStyle,
                        maxHeight: { md: "85%" },
                        scrollbarWidth: "none",
                      }}
                    >
                      {rewards.map(renderReward)}
                    </Box>
                  )}
                  {/* Close Button */}
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: "-2%",
                      left: "50%",
                      transform: "translateX(-50%)",
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <NeoButton
                      isIcon={true}
                      icon={<Close />}
                      color={Theme.palette.secondary.main}
                      onClick={onClose}
                    />
                  </Box>
                </Box>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </Modal>

      {/* Nested Modals */}
      <PackOpeningModal
        visible={packModalVisible}
        onClose={() => {
          setPackModalVisible(false);
          finalizeClaim(claimingReward.id);
        }}
        reward={
          Array.isArray(claimingReward?.reward?.pack)
            ? claimingReward?.reward?.pack
            : claimingReward?.reward?.pack?.cards
        }
      />

      {cardReward && (
        <CardRewardModal
          visible={cardModalVisible}
          reward={cardReward}
          onClose={() => {
            setCardModalVisible(false);
            finalizeClaim(cardReward.id);
            setCardReward(null);
          }}
        />
      )}
      <TokenRewardModal
        visible={tokenModalVisible}
        amount={claimingReward?.amount}
        onClose={() => {
          setTokenModalVisible(false);
          finalizeClaim(claimingReward?.id);
        }}
      />
    </>
  );
}
